import * as React from 'react'
import styled from 'styled-components'
import { screenSizes } from '@src/styles/theme'

type Props = {
  show: boolean
  onReload: () => void
  onCancel: () => void
}

const Overlay = styled.div<{ $show: boolean }>`
  display: ${(p) => p.$show ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999999;
  align-items: center;
  justify-content: center;
`

const Dialog = styled.div`
  background: ${(p) => p.theme.modalBack};
  color: ${(p) => p.theme.white};
  border-radius: ${(p) => p.theme.radius};
  width: 90%;
  max-width: 500px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);

  @media (max-width: ${screenSizes.XS}px) {
    padding: 24px 20px;
    width: 94%;
  }
`

const Title = styled.h3`
  margin: 0 0 12px;
  font-size: 18px;
  font-family: 'NunitoSansSemiBold';
  color: ${(p) => p.theme.warning};
`

const Body = styled.p`
  color: ${(p) => p.theme.white};
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 24px;
`

const Actions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;

  @media (max-width: ${screenSizes.XS}px) {
    flex-direction: column;
  }
`

const PrimaryButton = styled.button`
  background: ${(p) => p.theme.button.primary.default};
  color: ${(p) => p.theme.white};
  border: none;
  border-radius: ${(p) => p.theme.radius};
  padding: 10px 24px;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  font-family: 'NunitoSansSemiBold';

  &:hover { background: ${(p) => p.theme.button.primary.hover}; }
  &:focus-visible { box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent}; outline: none; }
`

const CancelButton = styled.button`
  background: ${(p) => p.theme.button.cancel.default};
  color: ${(p) => p.theme.white};
  border: none;
  border-radius: ${(p) => p.theme.radius};
  padding: 10px 24px;
  font-size: 14px;
  cursor: pointer;
  min-height: 44px;
  font-family: 'NunitoSansSemiBold';

  &:hover { background: ${(p) => p.theme.button.cancel.hover}; }
  &:focus-visible { box-shadow: 0 0 0 2px ${(p) => p.theme.accent}; outline: none; }
`

export default function ConflictDialog({ show, onReload, onCancel }: Props) {
  const reloadRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!show) return
    reloadRef.current?.focus()

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [show, onCancel])

  return (
    <Overlay $show={show} onClick={onCancel}>
      <Dialog
        role="alertdialog"
        aria-labelledby="conflict-title"
        aria-describedby="conflict-body"
        onClick={(e) => e.stopPropagation()}
      >
        <Title id="conflict-title">Config Changed Externally</Title>
        <Body id="conflict-body">
          Another client modified the configuration since you loaded it.
          Reload to see the latest version? Unsaved changes will be lost.
        </Body>
        <Actions>
          <CancelButton type="button" onClick={onCancel}>Cancel</CancelButton>
          <PrimaryButton ref={reloadRef} type="button" onClick={onReload}>Reload</PrimaryButton>
        </Actions>
      </Dialog>
    </Overlay>
  )
}
